/* External */
import { differenceInSeconds, differenceInMinutes, setSeconds } from 'date-fns';

/* Utils */
import { getAllGenres, isEpisode } from '../metaDataUtils';
import parentalControlBlock from '../parentalControlUtils';

const handleEpisodeName = (content, isLive) => {
  let title;
  if (isEpisode(content)) {
    title = content?.title;
  } else if (isLive) {
    title = undefined;
  } else {
    title = undefined;
  }
  return title;
};

const handleContentType = (contentType) => ({
  schedules: 'live',
  contents: 'movie',
  shows: 'show',
}[contentType?.toLowerCase()] || contentType);

const handleZeroValues = (value) => (!value && value === 0 ? undefined : value);
const getAgeRating = (content, rest) => content?.rating?.toLowerCase() || rest?.rating?.toLowerCase() || '0';

export const formatContentData = ({
  content,
  rest,
}) => {
  const genres = getAllGenres(content);
  const isContentBlockedByParentalControl = parentalControlBlock(content);
  const contentType = content?.type || content?.schema;
  const contentDuration = content?.duration || rest?.duration;
  return {
    genre: genres,
    primaryGenre: genres?.split(',')[0],
    duration: contentDuration && Number.parseInt(contentDuration, 10),
    progress: rest?.progress,
    startProgressBar: rest?.startProgressBar,
    episode: {
      seasonNumber: handleZeroValues(content?.episode?.seasonNumber),
      episodeNumber: handleZeroValues(content?.episode?.number),
      episodeName: handleEpisodeName(content, rest?.isLive),
    },
    serieName: content?.episode?.showName,
    title: isEpisode(content) ? content?.episode?.showName : content?.title,
    playSource: rest?.playSource,
    type: handleContentType(contentType),
    vrioAssetId: content?.vrioAssetId,
    channelId: content?.channelId?.toLowerCase() || rest?.channelId?.toLowerCase(),
    channelName: content?.channelName?.toLowerCase() || rest?.channelName,
    contentLocked: content?.shouldDisplayLockIcon,
    contentProvider: rest?.contentProvider,
    contentAgeRating: getAgeRating(content, rest),
    ageRestrictedContent: isContentBlockedByParentalControl?.toString(),
    changeParentalControl: rest?.changeParentalControl?.toString(),
    isBetting: content?.interactivities?.bets?.toString() || 'false',
  };
};

export const transmissionContentType = (value) => ({
  movie: 'vod',
  contents: 'vod',
  schedules: 'live',
  live: 'live',
  shows: 'vod',
  episode: 'vod',
  channel: 'live',
  channels: 'live',
}[value?.toLowerCase()] || undefined);

export const playerType = (value, isLive, isSports) => {
  const type = {
    movie: 'movie',
    live: 'channel',
    show: 'serie',
    shows: 'serie',
    episode: 'serie',
  }[value?.toLowerCase()] || undefined;
  return (isLive || isSports) ? 'channel' : type;
};

export const getParentLander = (value) => ({
  inicio: 'homelander',
  home: 'homelander',
  'on demand': 'ondemandlander',
  'on-demand': 'ondemandlander',
  on_demand: 'ondemandlander',
  kids: 'kidslander',
  sports: 'sportslander',
  deportes: 'sportslander',
  live: 'livelander',
  profiles: 'homeprofiles',
  'my-page': 'mypagelander',
}[value?.toLowerCase()] || '');

export const formatPlaySource = ({
  parentLander,
  provider,
  section,
}) => {
  if (!parentLander) return undefined;
  if (provider) {
    return `${parentLander?.trim()}-${section?.trim()}-sublander-${provider?.trim()}`;
  }
  return `${parentLander?.trim()}-${section?.trim()}`;
};

export const formatTimeToStart = ({ startTime }) => {
  const diffTime = differenceInSeconds(startTime, new Date(), { roundingMethod: 'round' });
  return Number.parseInt(diffTime, 10);
};

export const getTagEventFromRoute = ({ path }) => path?.split('/')[2];

export const getLiveStartProgressBar = ({ startTime, endTime, currentTime }) => {
  if (!startTime || !endTime || !currentTime) {
    return { startProgressBar: undefined, duration: undefined };
  }

  const newStartTime = startTime && setSeconds(new Date(startTime), 0);
  const newEndTime = endTime && setSeconds(new Date(endTime), 0);
  const duration = differenceInMinutes(newEndTime, newStartTime);
  const currentValue = differenceInMinutes(currentTime, newStartTime);
  const startProgressBarInSeconds = Math.floor(currentValue * 60);

  return {
    startProgressBar: startProgressBarInSeconds,
    duration,
  };
};

export const getGtmPlatform = (value) => ({
  tizen: 'samsung',
}[value?.toLowerCase()] || value);
